import { tx } from '@transifex/native'
import dayjs, { ConfigType, Dayjs } from 'dayjs'

/**
 * THE central date/label formatter (PRO-86) — the single boundary through
 * which every spelled-out date word (month names, weekday names) reaches a
 * screen, a generated label, or an export.
 *
 * Why it exists: the app sets the GLOBAL dayjs locale from the user's
 * datetime-FORMAT preference (`DayjsI18n` / `me.datetimeFormatLocale`), so any
 * `.format()` carrying word tokens (`MMM`, `MMMM`, `ddd`, `dddd`, `ll`, `lll`,
 * `LL`…) renders those words in the FORMAT locale — a user who picked the
 * Finnish date format but reads the UI in English saw "heinä" months and
 * "· su"/"· ke" weekday tokens inside otherwise-English screens and generated
 * route labels (third+ sighting of this class).
 *
 * The rule: date WORDS follow the LANGUAGE of the UI strings around them —
 * the company/user language setting (`me.language`, applied to Transifex by
 * `App/Transifex.tsx`), falling back to English (the source-string language).
 * Numeric-only formats (`YYYY-MM-DD`, `HH:mm`, `L`…) carry no words and may
 * keep following the user's datetime-format preference.
 *
 * Every helper pins the locale PER CALL and never mutates the global dayjs
 * locale other screens rely on. Do not call `.format()` with word tokens
 * anywhere else — `scripts/hauler-copy-gate.sh` fails the build on new ones.
 */

/** Transifex UI locale → dayjs locale (only locales DayjsI18n loads). */
const TX_TO_DAYJS_LOCALE: Record<string, string> = {
  da: 'da',
  en: 'en',
  fi: 'fi',
  no_NO: 'nb',
  sv: 'sv',
}

/** The dayjs locale matching the active UI language (fallback: English). */
export const uiDateLocale = (): string =>
  TX_TO_DAYJS_LOCALE[tx.getCurrentLocale()] || 'en'

/**
 * A dayjs instance pinned to the UI-language locale — the only sanctioned
 * source for spelled-out date words outside this module.
 */
export const uiDayjs = (d?: ConfigType): Dayjs =>
  dayjs(d).locale(uiDateLocale())

/**
 * The twelve month names in the active UI language, index 0 = January
 * (matching dayjs' 0-based month index).
 *
 * Resolve these PER CALL — never freeze the result into a module-level
 * constant. The global dayjs locale is `en` until `DayjsI18n` applies the
 * user's locale in an effect, so anything that reads month names at import
 * time captures English and never updates (PRO-658). Reading via `localeData`
 * (not `.format('MMMM')`) keeps this off the `hauler-copy-gate` word-token
 * radar and keys the words to the UI language, per this module's contract.
 */
export const uiMonthNames = (): string[] => uiDayjs().localeData().months()

/** "17 Jul 2026" — compact date (tables, range labels). */
export const formatDate = (d: ConfigType): string =>
  uiDayjs(d).format('D MMM YYYY')

/** "17 Jul 2026, 14:05" — timestamps ("As of …", "Completed at"). */
export const formatDateTime = (d: ConfigType): string =>
  uiDayjs(d).format('D MMM YYYY, HH:mm')

/** "17 Jul 14:05" — dense timestamps (cards, live feeds). */
export const formatShortDateTime = (d: ConfigType): string =>
  uiDayjs(d).format('D MMM HH:mm')

/** "17 Jul" — yearless date (range starts). */
export const formatShortDate = (d: ConfigType): string =>
  uiDayjs(d).format('D MMM')

/** "July 2026" — month labels and month-picker inputs. */
export const formatMonth = (d: ConfigType): string =>
  uiDayjs(d).format('MMMM YYYY')

/** "Wed" — short weekday, e.g. inside generated route labels. */
export const formatWeekday = (d: ConfigType): string => uiDayjs(d).format('ddd')

/** "Wednesday" — full weekday (calendar headers). */
export const formatWeekdayLong = (d: ConfigType): string =>
  uiDayjs(d).format('dddd')

/** "Wed 17 Jul" — weekday-led date (live boards, day pickers). */
export const formatWeekdayDate = (d: ConfigType): string =>
  uiDayjs(d).format('ddd D MMM')

/**
 * Month label in the active UI language with a custom format. Kept for the
 * existing `src/utils/calendar.ts` call sites; prefer the named helpers.
 */
export const formatMonthLabel = (date: Dayjs, format = 'MMMM YYYY'): string =>
  uiDayjs(date).format(format)
